function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { INPUT, LABEL } from "../../components/form-styles";
import { BRAND_MERGE_FIELDS } from "../../functions/brand-kit";
import { SECTION_TYPES, SECTION_DEFAULTS, STARTERS, KINDS_WITH_PRODUCTS } from "./section-types";
import RichText, { textOf } from "./rich-text";
import ImagePicker from "./image-picker";
import PreviewPane from "./preview-pane";
import StylePanel, { applyStyleToHtml } from "./style-panel";
import { AiDraft } from "./ai-draft";
import { SaveBlockButton, InsertBlockPanel, blocksHidden, loadBlocks } from "./saved-blocks";

/*
 * Section composer (EMAIL_EDITOR_BRANDING.md §3; mockup email-composer-2026-09-11.html;
 * 2026-10-04 round: rich text, image sources, preview pane with device toggle +
 * "preview as a contact" + test send, section tools + warnings, per-email style
 * overrides, four new section types, saved blocks, AI drafting).
 *
 * The email = the workspace brand frame (masthead · accent · compliance
 * footer — NOT editable here) around an ordered list of sections, each a
 * small form. The right rail shows the server render with sample data —
 * the same ComposerRenderer the worker uses at send time.
 *
 * design = { frame:"brand", version:1, sections:[{type,...}], style?:{accent,textColor,background,buttonRadius,fontFamily} }
 *
 * Props: design, brand, kind, onChange(design), onPickStarter(kind),
 *        renderPreview(design, {personId?}) → {html,…}
 *        optional: triggerType, onSendTest(), testBusy, testNote {kind,text}, products [{title,price}]
 */

export { SECTION_TYPES, STARTERS };
export function emptyDesign() {
  return {
    frame: "brand",
    version: 1,
    sections: []
  };
}
export function starterDesign(kind) {
  return {
    frame: "brand",
    version: 1,
    sections: JSON.parse(JSON.stringify(STARTERS[kind] || STARTERS.custom))
  };
}
export function isComposerDesign(d) {
  return !!(d && typeof d === "object" && Array.isArray(d.sections) && (d.frame || "brand") === "brand");
}
export function parseDesign(json) {
  try {
    const d = typeof json === "string" ? JSON.parse(json) : json;
    return isComposerDesign(d) ? d : null;
  } catch (e) {
    return null;
  }
}
const lbl = {
  ...LABEL,
  margin: "8px 0 3px"
};
// margin 0: a global input rule adds a bottom margin to text inputs but not selects, so side-by-side fields misaligned.
const inp = {
  ...INPUT,
  margin: 0
};
const sel = {
  ...INPUT,
  width: "auto",
  margin: 0,
  height: 38,
  minHeight: 38,
  paddingTop: 0,
  paddingBottom: 0,
  paddingRight: 32
};
const MERGE = [["{{FirstName}}", "First name"], ...BRAND_MERGE_FIELDS.map(([v, l]) => [v, l.replace(/ from the brand kit| \(.*\)/g, "")])];
const SOCIAL = [["facebook", "Facebook"], ["instagram", "Instagram"], ["pinterest", "Pinterest"], ["x", "X"], ["website", "Website"]];
function summary(s) {
  switch (s.type) {
    case "hero":
      return s.headline || "(no headline yet)";
    case "text":
    case "footerText":
      return textOf(s.html).slice(0, 80) || "(empty)";
    case "products":
      return `Up to ${s.max || 3} · ${s.columns || 3} per row${s.showPrice === false ? "" : " · price"}${s.button ? ` · “${s.button}”` : ""}`;
    case "recommended":
      return `${s.title || "You might also like"} · up to ${s.max || 3}`;
    case "offer":
      return s.headline || s.code || "(no offer text yet)";
    case "button":
      return s.label ? `“${s.label}”${s.url ? ` → ${s.url}` : ""}` : "(no label yet)";
    case "image":
      return s.imageUrl ? (s.alt || "Image") + (s.url ? ` → ${s.url}` : "") : "(no image yet)";
    case "imageText":
      return textOf(s.html).slice(0, 60) || "(empty)";
    case "columns":
      return [textOf(s.leftHtml).slice(0, 36), textOf(s.rightHtml).slice(0, 36)].filter(Boolean).join(" | ") || "(empty)";
    case "social":
      {
        const n = SOCIAL.filter(([k]) => (s[k] || "").trim()).length;
        return n ? `${n} link${n === 1 ? "" : "s"}` : "(no links yet)";
      }
    case "spacer":
      return `${s.height || 24}px`;
    default:
      return "";
  }
}

/** Inline warnings for one section (shown as a line + count badge). */
export function sectionWarnings(s, kind) {
  const w = [];
  const hasText = h => !!textOf(h);
  switch (s.type) {
    case "hero":
      if (!(s.headline || "").trim()) w.push("No headline yet.");
      if ((s.label || "").trim() && !(s.url || "").trim()) w.push("The button has a label but no link.");
      break;
    case "button":
      if ((s.label || "").trim() && !(s.url || "").trim()) w.push("The button has a label but no link.");
      if (!(s.label || "").trim()) w.push("No button label yet.");
      break;
    case "offer":
      if ((s.label || "").trim() && !(s.url || "").trim()) w.push("The button has a label but no link.");
      if (!(s.headline || "").trim() && !hasText(s.html)) w.push("The offer is empty.");
      break;
    case "products":
      if (kind && KINDS_WITH_PRODUCTS.indexOf(kind) < 0) w.push("This trigger carries no products: the section is skipped for every recipient.");
      break;
    case "text":
    case "footerText":
    case "imageText":
      if (!hasText(s.html)) w.push("No text yet.");
      break;
    case "image":
      if (!s.imageUrl) w.push("No image yet.");else if (!(s.alt || "").trim()) w.push("Add a short description so people with images turned off know what it shows.");
      break;
    case "columns":
      if (!hasText(s.leftHtml) && !hasText(s.rightHtml)) w.push("Both columns are empty.");
      break;
    case "social":
      if (!SOCIAL.some(([k]) => (s[k] || "").trim())) w.push("No links yet.");
      break;
    default:
      break;
  }
  return w;
}
export default class Composer extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "setStyle", style => {
      const d = {
        ...this.props.design
      };
      if (style) d.style = style;else delete d.style;
      this.props.onChange(d);
    });
    _defineProperty(this, "setPerson", person => {
      this.setState({
        person
      }, () => {
        clearTimeout(this._pvT);
        this.renderNow();
      });
    });
    this.state = {
      open: {
        0: true
      },
      preview: null,
      previewBusy: false,
      previewErr: null,
      showMenu: false,
      menuMode: "types",
      drag: null,
      over: null,
      person: null,
      previewProducts: null,
      blocksHidden: blocksHidden()
    };
    this._alive = true;
    this._rt = {};
  }
  componentDidMount() {
    this.schedulePreview();
    loadBlocks().then(() => this._alive && this.setState({
      blocksHidden: blocksHidden()
    })).catch(() => {});
  }
  componentDidUpdate(prev) {
    if (prev.design !== this.props.design) {
      const j = JSON.stringify(this.props.design.sections || []);
      if (j !== this._sectionsJson) this.schedulePreview();
    }
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._pvT);
  }
  schedulePreview() {
    clearTimeout(this._pvT);
    this._sectionsJson = JSON.stringify(this.props.design.sections || []);
    this._pvT = setTimeout(() => this.renderNow(), 450);
  }
  async renderNow() {
    if (!this._alive || !this.props.renderPreview) return;
    const seq = this._pvSeq = (this._pvSeq || 0) + 1;
    this.setState({
      previewBusy: true
    });
    try {
      const person = this.state.person;
      const r = await this.props.renderPreview(this.props.design, person ? {
        personId: person.id
      } : undefined);
      if (!this._alive || seq !== this._pvSeq) return;
      const products = r && Array.isArray(r.products) ? r.products.map(p => ({
        title: p.title || p.name || "",
        price: p.price
      })).filter(p => p.title) : null;
      this.setState({
        preview: r && r.html,
        previewBusy: false,
        previewErr: null,
        previewProducts: products || this.state.previewProducts,
        previewText: textOf(r && r.html)
      });
    } catch (e) {
      if (this._alive && seq === this._pvSeq) this.setState({
        previewBusy: false,
        previewErr: e && e.message || "Preview failed."
      });
    }
  }

  // ---- section ops (open-state map follows the index moves) ----
  emit(sections, open) {
    this.props.onChange({
      ...this.props.design,
      sections
    });
    if (open) this.setState({
      open
    });
  }
  update(i, patch) {
    this.emit(this.props.design.sections.map((s, k) => k === i ? {
      ...s,
      ...patch
    } : s));
  }
  move(i, dir) {
    const sections = [...this.props.design.sections];
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[i], sections[j]] = [sections[j], sections[i]];
    const open = {
      ...this.state.open
    };
    const a = !!open[i],
      b = !!open[j];
    open[i] = b;
    open[j] = a;
    this.emit(sections, open);
  }
  reorder(from, to) {
    if (from === to || from == null || to == null) return;
    const sections = [...this.props.design.sections];
    const [s] = sections.splice(from, 1);
    sections.splice(to, 0, s);
    const was = Object.keys(this.state.open).filter(k => this.state.open[k]).map(Number);
    const open = {};
    was.forEach(k => {
      let n = k;
      if (k === from) n = to;else {
        if (k > from) n -= 1;
        if (n >= to) n += 1;
      }
      open[n] = true;
    });
    this.emit(sections, open);
  }
  remove(i) {
    const sections = this.props.design.sections.filter((_, k) => k !== i);
    const open = {};
    Object.keys(this.state.open).forEach(k => {
      const n = Number(k);
      if (!this.state.open[k] || n === i) return;
      open[n > i ? n - 1 : n] = true;
    });
    this.emit(sections, open);
  }
  duplicate(i) {
    const sections = [...this.props.design.sections];
    sections.splice(i + 1, 0, JSON.parse(JSON.stringify(sections[i])));
    const open = {};
    Object.keys(this.state.open).forEach(k => {
      const n = Number(k);
      if (!this.state.open[k]) return;
      open[n > i ? n + 1 : n] = true;
    });
    open[i + 1] = true;
    this.emit(sections, open);
  }
  insert(section) {
    const sections = [...this.props.design.sections, section];
    this.emit(sections, {
      ...this.state.open,
      [sections.length - 1]: true
    });
    this.setState({
      showMenu: false,
      menuMode: "types"
    });
  }
  add(type) {
    this.insert({
      type,
      ...JSON.parse(JSON.stringify(SECTION_DEFAULTS[type] || {}))
    });
  }
  setAll(on) {
    const open = {};
    if (on) (this.props.design.sections || []).forEach((_, i) => {
      open[i] = true;
    });
    this.setState({
      open
    });
  }
  toggle(i) {
    this.setState({
      open: {
        ...this.state.open,
        [i]: !this.state.open[i]
      }
    });
  }
  insertMerge(i, field, token) {
    const rt = this._rt[`${i}:${field}`];
    if (rt && rt.insertText) {
      rt.insertText(token);
      return;
    }
    const s = this.props.design.sections[i];
    const cur = s[field] || "";
    this.update(i, {
      [field]: cur + token
    });
  }
  productsInPlay() {
    const has = (this.props.design.sections || []).some(s => s.type === "products" || s.type === "recommended");
    if (!has) return [];
    return (this.props.products && this.props.products.length ? this.props.products : this.state.previewProducts || []).slice(0, 3);
  }

  // A new section type the mail service doesn't render yet shows up as missing text in the preview.
  notRendered(s) {
    const t = this.state.previewText;
    if (!t || !this.state.preview) return false;
    const probe = s.type === "columns" ? textOf(s.leftHtml) || textOf(s.rightHtml) : s.type === "social" ? SOCIAL.map(([k, l]) => (s[k] || "").trim() ? l : "").filter(Boolean)[0] : s.type === "footerText" ? textOf(s.html) : "";
    if (!probe) return false;
    const needle = probe.replace(/\{\{[^}]+\}\}/g, " ").trim().slice(0, 40).toLowerCase();
    return needle.length >= 3 && t.toLowerCase().indexOf(needle) < 0;
  }

  // ---- per-section editors ----
  form(s, i) {
    const t = (f, label, extra) => /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, label), /*#__PURE__*/React.createElement("input", _extends({
      style: inp,
      value: s[f] || "",
      onChange: e => this.update(i, {
        [f]: e.target.value
      })
    }, extra || {})));
    const merge = (f, only) => /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 4,
        display: "flex",
        gap: 4,
        flexWrap: "wrap",
        alignItems: "center"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 11,
        color: "var(--plk-text-dim)"
      }
    }, "Insert:"), (only ? MERGE.slice(0, only) : MERGE).map(([v, l]) => /*#__PURE__*/React.createElement("a", {
      key: v,
      className: "mtf-chip",
      style: {
        cursor: "pointer",
        fontSize: 11
      },
      onClick: () => this.insertMerge(i, f, v)
    }, l)));
    const rich = (f, label, rows, placeholder) => /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
      style: lbl
    }, label), /*#__PURE__*/React.createElement(RichText, {
      ref: r => {
        this._rt[`${i}:${f}`] = r;
      },
      value: s[f] || "",
      rows: rows,
      placeholder: placeholder,
      onChange: html => this.update(i, {
        [f]: html
      })
    }), merge(f));
    const cta = () => /*#__PURE__*/React.createElement("div", {
      style: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10
      }
    }, t("label", "Button label"), t("url", "Button link", {
      placeholder: "https://…"
    }));
    const ai = (sectionType, fields, current, map) => /*#__PURE__*/React.createElement(AiDraft, {
      sectionType: sectionType,
      kind: this.props.kind,
      triggerType: this.props.triggerType,
      fields: fields,
      current: current,
      products: this.productsInPlay(),
      onUse: patch => this.update(i, map ? map(patch) : patch)
    });
    const newNote = this.notRendered(s) ? /*#__PURE__*/React.createElement("p", {
      className: "cmp-newtype"
    }, "New section type: it renders when the mail service is updated; the preview may not show it yet.") : null;
    switch (s.type) {
      case "hero":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 6
          }
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Headline"), /*#__PURE__*/React.createElement("input", {
          style: inp,
          value: s.headline || "",
          onChange: e => this.update(i, {
            headline: e.target.value
          })
        }), merge("headline", 2)), t("subhead", "Subhead"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Image"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.imageFrom || "none",
          onChange: e => this.update(i, {
            imageFrom: e.target.value
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "none"
        }, "None"), /*#__PURE__*/React.createElement("option", {
          value: "firstProduct"
        }, "First product from the trigger"), /*#__PURE__*/React.createElement("option", {
          value: "url"
        }, "Choose an image\u2026")), s.imageFrom === "url" && /*#__PURE__*/React.createElement(ImagePicker, {
          value: s.imageUrl || "",
          brand: this.props.brand,
          onChange: (url, meta) => this.update(i, {
            imageUrl: url,
            imageMeta: url ? meta : undefined
          })
        })), cta(), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Align"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.align || "left",
          onChange: e => this.update(i, {
            align: e.target.value
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "left"
        }, "Left"), /*#__PURE__*/React.createElement("option", {
          value: "center"
        }, "Center")))), ai("hero", ["headline", "subhead"], {
          headline: s.headline || "",
          subhead: s.subhead || "",
          label: s.label || ""
        }));
      case "text":
        return /*#__PURE__*/React.createElement(React.Fragment, null, rich("html", "Text", 4), ai("text", ["html"], {
          html: s.html || ""
        }));
      case "products":
        return /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "repeat(4, auto)",
            gap: 10,
            alignItems: "end"
          }
        }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Show up to"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.max || 3,
          onChange: e => this.update(i, {
            max: Number(e.target.value)
          })
        }, [1, 2, 3, 4, 6].map(n => /*#__PURE__*/React.createElement("option", {
          key: n,
          value: n
        }, n)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Per row"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.columns || 3,
          onChange: e => this.update(i, {
            columns: Number(e.target.value)
          })
        }, [1, 2, 3].map(n => /*#__PURE__*/React.createElement("option", {
          key: n,
          value: n
        }, n)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Price"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.showPrice === false ? "no" : "yes",
          onChange: e => this.update(i, {
            showPrice: e.target.value === "yes"
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "yes"
        }, "Show"), /*#__PURE__*/React.createElement("option", {
          value: "no"
        }, "Hide"))), t("button", "Card button", {
          placeholder: "View"
        }), /*#__PURE__*/React.createElement("p", {
          className: "territory-hint",
          style: {
            gridColumn: "1 / -1",
            margin: 0
          }
        }, "Skipped automatically for a recipient with no trigger products."));
      case "recommended":
        return /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "2fr auto auto auto",
            gap: 10,
            alignItems: "end"
          }
        }, t("title", "Heading", {
          placeholder: "You might also like"
        }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Show up to"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.max || 3,
          onChange: e => this.update(i, {
            max: Number(e.target.value)
          })
        }, [1, 2, 3, 4, 6].map(n => /*#__PURE__*/React.createElement("option", {
          key: n,
          value: n
        }, n)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Per row"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.columns || 3,
          onChange: e => this.update(i, {
            columns: Number(e.target.value)
          })
        }, [1, 2, 3].map(n => /*#__PURE__*/React.createElement("option", {
          key: n,
          value: n
        }, n)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Price"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.showPrice === false ? "no" : "yes",
          onChange: e => this.update(i, {
            showPrice: e.target.value === "yes"
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "yes"
        }, "Show"), /*#__PURE__*/React.createElement("option", {
          value: "no"
        }, "Hide"))), /*#__PURE__*/React.createElement("p", {
          className: "territory-hint",
          style: {
            gridColumn: "1 / -1",
            margin: 0
          }
        }, "From the co-purchase model, seeded by the trigger products; skipped when there's nothing to recommend."));
      case "offer":
        return /*#__PURE__*/React.createElement(React.Fragment, null, t("headline", "Offer headline"), rich("html", "Details", 2), /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10
          }
        }, t("code", "Code", {
          placeholder: "optional"
        }), t("label", "Button label"), t("url", "Button link")), ai("offer", ["headline", "html", "buttonLabel"], {
          headline: s.headline || "",
          html: s.html || "",
          label: s.label || ""
        }, p => {
          const q = {
            ...p
          };
          if (q.buttonLabel != null) {
            q.label = q.buttonLabel;
            delete q.buttonLabel;
          }
          return q;
        }));
      case "button":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 10,
            alignItems: "end"
          }
        }, t("label", "Label"), t("url", "Link", {
          placeholder: "https://…"
        }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Align"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.align || "center",
          onChange: e => this.update(i, {
            align: e.target.value
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "left"
        }, "Left"), /*#__PURE__*/React.createElement("option", {
          value: "center"
        }, "Center")))), ai("button", ["buttonLabel"], {
          label: s.label || "",
          url: s.url || ""
        }, p => ({
          label: p.buttonLabel
        })));
      case "image":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ImagePicker, {
          label: "Image",
          value: s.imageUrl || "",
          brand: this.props.brand,
          onChange: (url, meta) => this.update(i, {
            imageUrl: url,
            imageMeta: url ? meta : undefined
          })
        }), /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 10,
            alignItems: "end"
          }
        }, t("alt", "Description", {
          placeholder: "What the picture shows"
        }), t("url", "Link (optional)", {
          placeholder: "https://…"
        }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Size"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.width || "full",
          onChange: e => this.update(i, {
            width: e.target.value
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "full"
        }, "Full width"), /*#__PURE__*/React.createElement("option", {
          value: "wide"
        }, "Wide"), /*#__PURE__*/React.createElement("option", {
          value: "medium"
        }, "Medium")))));
      case "imageText":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ImagePicker, {
          label: "Image",
          value: s.imageUrl || "",
          brand: this.props.brand,
          onChange: (url, meta) => this.update(i, {
            imageUrl: url,
            imageMeta: url ? meta : undefined
          })
        }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("label", {
          style: lbl
        }, "Image side"), /*#__PURE__*/React.createElement("select", {
          style: sel,
          value: s.imageSide || "left",
          onChange: e => this.update(i, {
            imageSide: e.target.value
          })
        }, /*#__PURE__*/React.createElement("option", {
          value: "left"
        }, "Left"), /*#__PURE__*/React.createElement("option", {
          value: "right"
        }, "Right"))), rich("html", "Text", 3));
      case "columns":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10
          }
        }, rich("leftHtml", "Left column", 3), rich("rightHtml", "Right column", 3)), newNote);
      case "social":
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          style: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10
          }
        }, SOCIAL.map(([k, l]) => /*#__PURE__*/React.createElement(React.Fragment, {
          key: k
        }, t(k, l, {
          placeholder: k === "website" ? "https://… or {{brandWebsite}}" : `https://${k === "x" ? "x.com" : k + ".com"}/…`
        })))), /*#__PURE__*/React.createElement("p", {
          className: "territory-hint",
          style: {
            margin: "6px 0 0"
          }
        }, "Renders as a row of labeled links; leave a network blank to hide it."), newNote);
      case "footerText":
        return /*#__PURE__*/React.createElement(React.Fragment, null, rich("html", "Footer note (small, muted)", 2, "Opening hours, your address, a legal line…"), newNote);
      case "spacer":
        return /*#__PURE__*/React.createElement("div", {
          style: {
            display: "flex",
            gap: 10,
            alignItems: "center"
          }
        }, /*#__PURE__*/React.createElement("label", {
          style: {
            ...lbl,
            margin: 0
          }
        }, "Height"), /*#__PURE__*/React.createElement("input", {
          type: "range",
          min: "8",
          max: "64",
          step: "4",
          value: s.height || 24,
          onChange: e => this.update(i, {
            height: Number(e.target.value)
          }),
          style: {
            width: 180
          }
        }), /*#__PURE__*/React.createElement("span", {
          style: {
            fontSize: 12
          }
        }, s.height || 24, "px"));
      default:
        return /*#__PURE__*/React.createElement("p", {
          className: "territory-hint",
          style: {
            margin: 0
          }
        }, "Nothing to configure.");
    }
  }
  render() {
    const {
      design,
      brand,
      onPickStarter,
      kind,
      onSendTest,
      testBusy,
      testNote
    } = this.props;
    const {
      open,
      preview,
      previewBusy,
      previewErr,
      showMenu,
      menuMode,
      person
    } = this.state;
    const sections = design.sections || [];
    const anyOpen = sections.some((_, i) => open[i]);
    return /*#__PURE__*/React.createElement("div", {
      className: "cmp",
      style: {
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: "1 1 460px",
        minWidth: 0
      }
    }, !sections.length && /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px dashed var(--plk-border)",
        borderRadius: 10,
        padding: 14,
        marginBottom: 10,
        background: "var(--plk-surface-alt)"
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: 13
      }
    }, "Start from a template"), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "4px 0 8px"
      }
    }, "Each starter is a short list of sections in your brand frame. Change anything after."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        flexWrap: "wrap"
      }
    }, [["ProductViewed", "Products viewed"], ["CartAbandoned", "Cart recovery"], ["WelcomeSeries", "Welcome"], ["CustomerLoyalty", "Loyalty / thank you"], ["custom", "Blank note"]].map(([k, l]) => /*#__PURE__*/React.createElement("a", {
      key: k,
      className: "mtf-chip" + (k === kind ? " on" : ""),
      style: {
        cursor: "pointer"
      },
      onClick: () => onPickStarter(k)
    }, l)))), /*#__PURE__*/React.createElement(StylePanel, {
      style: design.style,
      brand: brand,
      onChange: this.setStyle
    }), sections.length ? /*#__PURE__*/React.createElement("div", {
      className: "cmp-listbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, sections.length, " section", sections.length === 1 ? "" : "s"), /*#__PURE__*/React.createElement("a", {
      className: "cmp-link",
      onClick: () => this.setAll(!anyOpen)
    }, anyOpen ? "Collapse all" : "Expand all")) : null, sections.map((s, i) => {
      const meta = SECTION_TYPES.find(([t]) => t === s.type) || [s.type, s.type, ""];
      const on = !!open[i];
      const {
        drag,
        over
      } = this.state;
      const isOver = drag != null && over === i && drag !== i;
      const warns = sectionWarnings(s, kind);
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: "cmp-section" + (on || isOver ? " on" : ""),
        draggable: true,
        onDragStart: e => {
          try {
            e.dataTransfer.setData("text/plain", String(i));
            e.dataTransfer.effectAllowed = "move";
          } catch (x) {/* noop */}
          this.setState({
            drag: i
          });
        },
        onDragEnd: () => this.setState({
          drag: null,
          over: null
        }),
        onDragOver: e => {
          if (drag == null) return;
          e.preventDefault();
          if (over !== i) this.setState({
            over: i
          });
        },
        onDrop: e => {
          e.preventDefault();
          const from = this.state.drag;
          this.setState({
            drag: null,
            over: null
          });
          if (from != null) this.reorder(from, i);
        },
        style: {
          opacity: drag === i ? 0.45 : 1,
          borderTopWidth: isOver && drag > i ? 3 : 1,
          borderBottomWidth: isOver && drag < i ? 3 : 1
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-head",
        onClick: () => this.toggle(i)
      }, /*#__PURE__*/React.createElement("span", {
        className: "cmp-grip",
        title: "Drag to reorder"
      }, "\u22EE\u22EE"), /*#__PURE__*/React.createElement("span", {
        className: "cmp-caret"
      }, on ? "▾" : "▸"), /*#__PURE__*/React.createElement("div", {
        style: {
          minWidth: 0,
          flex: "1 1 auto"
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-title"
      }, meta[1], warns.length ? /*#__PURE__*/React.createElement("span", {
        className: "cmp-badge",
        title: warns.join(" ")
      }, warns.length) : null), /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-sum"
      }, summary(s))), /*#__PURE__*/React.createElement("span", {
        className: "cmp-section-tools",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: i === 0,
        onClick: () => this.move(i, -1),
        title: "Move up"
      }, "\u25B2"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        disabled: i === sections.length - 1,
        onClick: () => this.move(i, 1),
        title: "Move down"
      }, "\u25BC"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.duplicate(i),
        title: "Duplicate"
      }, "\u29C9"), /*#__PURE__*/React.createElement("button", {
        type: "button",
        onClick: () => this.remove(i),
        title: "Remove"
      }, "\xD7"))), warns.length ? /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-warn"
      }, warns.join(" ")) : null, on && /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-body"
      }, this.form(s, i), /*#__PURE__*/React.createElement("div", {
        className: "cmp-section-foot"
      }, /*#__PURE__*/React.createElement(SaveBlockButton, {
        section: s
      }), /*#__PURE__*/React.createElement("span", {
        className: "cmp-section-foot-tools"
      }, /*#__PURE__*/React.createElement("a", {
        className: "cmp-link dim",
        onClick: () => this.duplicate(i)
      }, "Duplicate"), /*#__PURE__*/React.createElement("a", {
        className: "cmp-link danger",
        onClick: () => this.remove(i)
      }, "Remove")))));
    }), /*#__PURE__*/React.createElement("div", {
      style: {
        border: "2px dashed var(--plk-border)",
        borderRadius: 10,
        padding: 10,
        textAlign: "center"
      }
    }, !showMenu ? /*#__PURE__*/React.createElement("a", {
      className: "mtf-chip",
      style: {
        cursor: "pointer"
      },
      onClick: () => this.setState({
        showMenu: true,
        menuMode: "types"
      })
    }, "\uFF0B Add a section") : menuMode === "blocks" ? /*#__PURE__*/React.createElement(InsertBlockPanel, {
      onInsert: section => this.insert(section),
      onClose: () => this.setState({
        showMenu: false,
        menuMode: "types"
      })
    }) : /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        justifyContent: "center"
      }
    }, SECTION_TYPES.map(([t, l, d]) => /*#__PURE__*/React.createElement("a", {
      key: t,
      className: "mtf-chip",
      style: {
        cursor: "pointer"
      },
      title: d,
      onClick: () => this.add(t)
    }, l)), !this.state.blocksHidden ? /*#__PURE__*/React.createElement("a", {
      className: "mtf-chip cmp-chip-block",
      style: {
        cursor: "pointer"
      },
      title: "A section saved earlier for this workspace",
      onClick: () => this.setState({
        menuMode: "blocks"
      })
    }, "Insert saved block\u2026") : null, /*#__PURE__*/React.createElement("a", {
      className: "mtf-chip",
      style: {
        cursor: "pointer",
        color: "var(--plk-text-dim)"
      },
      onClick: () => this.setState({
        showMenu: false
      })
    }, "cancel"))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "10px 0 0"
      }
    }, "Masthead, logo and the compliance footer come from ", /*#__PURE__*/React.createElement("a", {
      href: "/Settings/Brand"
    }, "Settings \u2192 Brand kit"), brand && brand.company ? ` (${brand.company})` : "", ". Change them once; every automation follows.")), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: "0 0 400px",
        maxWidth: "100%"
      }
    }, /*#__PURE__*/React.createElement(PreviewPane, {
      html: applyStyleToHtml(preview, design.style),
      busy: previewBusy,
      renderErr: previewErr,
      person: person,
      onPerson: this.setPerson,
      onSendTest: onSendTest,
      testBusy: testBusy,
      testNote: testNote
    })));
  }
}